/* eslint-disable max-lines */

import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
} from "react";

import { useQuery } from "@apollo/client";

import { ArrowLeftIcon } from "@/components/icons/ArrowLeftIcon";
import { CloseIcon } from "@/components/icons/CloseIcon";
import { SearchIcon } from "@/components/icons/SearchIcon";
import LoadingSpinner from "@/components/ui-kit/Loading/LoadingSpinner/LoadingSpinner";
import { TextElement } from "@/components/ui-kit/TextElement/TextElement";
import SearchContext from "@/context/SearchContext";
import UserContext from "@/context/UserContext";
import { useIsScrollTopBottom } from "@/hooks/useIsScrollTopBottom";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { getCustomerOrdersAndSubscriptions } from "@/queries/accountorders";
import { AlgoliaResult, ResultSearchVisible, SuggestedItem } from "@/types/search";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

import QueryText from "./QueryText";
import styles from "./Search.module.scss";
import SearchResults from "./SearchResults";
import { getSubscriptionState, reformatSubscriptions } from "./utils";

const Search: React.FC = () => {
  const { isLoggedIn } = useContext(UserContext);

  const {
    searchClient,
    search,
    setSearch,
    searchState,
    setSearchState,
    resultSearchVisible,
    setResultSearchVisible,
    setSubscriptionsAndOrders,
    closeSearchDropdown,
    resultsFound,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchClient: any;
    search: string;
    setSearch: (value: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchState: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSearchState: (state: any) => void;
    resultSearchVisible: ResultSearchVisible;
    setResultSearchVisible: (val: ResultSearchVisible) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSubscriptionsAndOrders: (val: any) => void;
    closeSearchDropdown: () => void;
    resultsFound: number;
  } = useContext(SearchContext);

  const [isSearchMounted, setIsSearchMounted] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [searchedItem, setSearchItem] = useState<any>({});
  const scrollContainer = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchData, setSearchData] = useState<AlgoliaResult[]>([]);
  const [isLoadingAlgolia, setIsLoadingAlgolia] = useState<boolean>(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState<boolean>(true);
  const resultShownSegmentTimer = useRef<NodeJS.Timeout>(null);

  const { isTop, isBottom, onScrollHandler } = useIsScrollTopBottom();

  const searchRef = useOutsideClick<HTMLDivElement>(() => {
    if (isSearchMounted) {
      closeSearchDropdown();
    }
  });

  const [suggestions] = searchData?.filter(
    (suggestion) =>
      suggestion.index === "Products_query_suggestions" ||
      suggestion.index === "Zendesk_query_suggestions"
  );

  // Cast hits to SuggestedItem[] explicitly:
  const suggestedHits: SuggestedItem[] = (suggestions?.hits ?? []) as SuggestedItem[];

  const { data: subscriptionsAndOrders, loading: isLoadingSubscriptions } = useQuery(
    getCustomerOrdersAndSubscriptions,
    {
      skip: !isLoggedIn,
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    if (!isLoadingAlgolia && !isLoadingSubscriptions) {
      setIsLoadingSearch(false);
    }
  }, [isLoadingAlgolia, isLoadingSubscriptions]);

  useEffect(() => {
    if (scrollContainer.current) {
      onScrollHandler(scrollContainer.current);
    }
  }, [onScrollHandler, scrollContainer]);

  const getSearchResultsFromAlgolia = useCallback(
    async (query: string) => {
      const queries = [
        {
          indexName: process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_NAME ?? "Products",
          query,
          params: {
            hitsPerPage: 4,
            clickAnalytics: true,
          },
        },
        {
          indexName: process.env.NEXT_PUBLIC_ALGOLIA_ZENDESK_INDEX_NAME ?? "Zendesk_production",
          query,
          params: {
            hitsPerPage: 10,
            clickAnalytics: true,
          },
        },
        {
          indexName: process.env.NEXT_PUBLIC_ALGOLIA_PAGES_INDEX_NAME ?? "Pages",
          query,
          params: {
            hitsPerPage: 3,
            clickAnalytics: true,
          },
        },
        {
          indexName: "Products_query_suggestions",
          query,
        },
        {
          indexName: "Zendesk_query_suggestions",
          query,
        },
      ];

      try {
        setIsLoadingAlgolia(true);
        const { results } = await searchClient.search(queries);
        setSearchData(results);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Algolia API error multipleQueries ${error}`);
      } finally {
        setIsLoadingAlgolia(false);
      }

      setResultSearchVisible({ product: false, article: false });
    },
    [searchClient, setResultSearchVisible]
  );

  useEffect(() => {
    getSearchResultsFromAlgolia(search);
  }, [getSearchResultsFromAlgolia, search]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [inputRef]);

  useEffect(() => {
    if (!isLoadingSubscriptions) {
      if (subscriptionsAndOrders) {
        setSubscriptionsAndOrders(reformatSubscriptions(subscriptionsAndOrders));
      }
      const subState = getSubscriptionState(subscriptionsAndOrders);
      setSearchState(subState);
    }
  }, [isLoadingSubscriptions, setSearchState, setSubscriptionsAndOrders, subscriptionsAndOrders]);

  useEffect(() => {
    setIsSearchMounted(true);
  }, []);

  const onHandleEnterKeyFromSearchedItem = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    const searchValue = (event.target as HTMLInputElement).value;
    GA4.onSearchEntered({
      name: "Search",
      location: "navigation",
      state: searchState,
      search_term: searchValue,
    });
    GA4.onSearchResultsShown({
      name: "Search",
      location: "navigation",
      search_term: searchValue,
      results_found: !!resultsFound,
      num_search_results: resultsFound,
      state: "results",
    });
    setSearch(searchValue);
    getSearchResultsFromAlgolia(searchValue);
    inputRef.current?.blur();
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (search.length === 0 && e.target.value.length) {
      GA4.onSearchStarted({
        name: "Search",
        location: "navigation",
        state: searchState,
        search_term: e.target.value,
      });
    }
    if (search.length > 0 && e.target.value.length === 0) {
      segmentEvent("Search Cleared", {
        name: "Search",
        location: "navigation",
        state: searchState,
      });
    }
    if (e.target.value.length) {
      if (resultShownSegmentTimer.current) {
        clearTimeout(resultShownSegmentTimer.current);
      }
      resultShownSegmentTimer.current = setTimeout(() => {
        GA4.onSearchResultsShown({
          name: "Search",
          location: "navigation",
          search_term: e.target.value,
          results_found: !!resultsFound,
          num_search_results: resultsFound,
          state: "results",
        });
      }, 500);
    }
    setSearch(e.target.value);
  };

  return (
    <div ref={searchRef}>
      <div
        ref={scrollContainer}
        onScroll={(e) => onScrollHandler(e.target as HTMLElement)}
        className={cn("flex flex-col overflow-auto  w-full z-90", styles.SearchContainer)}
      >
        <div
          className={cn(
            "px-4 md:px-6 lg:px-10 lg:pt-4 xl:px-[80px] ",
            !isTop && styles.ScrollShadow,
            "sticky md:relative top-0 left-0  z-10"
          )}
        >
          <div className="flex items-center -mx-4 md:mx-0 lg:pb-2">
            {(resultSearchVisible.product || resultSearchVisible.article) && (
              <button
                type="button"
                className={cn(
                  "inline-flex items-center h-[55px] px-4 lg:h-12 md:px-0 text-[14px] lg:text-[16px]",
                  styles.SearchButton
                )}
                onClick={() => setResultSearchVisible({ article: false, product: false })}
                aria-label="Back"
              >
                <ArrowLeftIcon className="text-nova-transparency-black-60" />
                <TextElement text="Back" element="span" className="ml-2 text-master-base-alt-alt" />
              </button>
            )}
            <button
              className="ml-auto inline-flex items-center h-[55px] px-4 lg:h-12 md:px-0"
              onClick={closeSearchDropdown}
              aria-label="Close"
            >
              <TextElement
                text="Close"
                element="span"
                className="text-master-base-alt-alt body--large"
              />
            </button>
          </div>
          <div className="relative flex items-center mb-2 md:mb-4">
            <input
              name="search"
              id="search"
              type="text"
              autoComplete="search"
              value={search}
              ref={inputRef}
              maxLength={50}
              onChange={onChangeHandler}
              placeholder="Search for products and articles"
              className={cn(
                "w-full rounded-lg bg-beige  border border-[#90a9a6] outline-none font-grilliBook leadingNone",
                styles.SearchInput
              )}
              onKeyDown={onHandleEnterKeyFromSearchedItem}
              aria-label="Search"
            />
            <div className="absolute right-4 lg:right-6 flex items-center gap-2 lg:gap-4">
              {Boolean(search.length) && (
                <button
                  onClick={() => {
                    segmentEvent("Search Cleared", {
                      name: "Search",
                      location: "navigation",
                      state: searchState,
                    });
                    setSearch("");
                    inputRef.current?.focus();
                  }}
                  className={styles.ClearIcon}
                  aria-label="Clear search"
                >
                  <CloseIcon />
                </button>
              )}
              <button
                onClick={() => {
                  GA4.onSearchEntered({
                    name: "Search",
                    location: "navigation",
                    state: searchState,
                    search_term: search,
                  });
                  GA4.onSearchResultsShown({
                    name: "Search",
                    location: "navigation",
                    search_term: search,
                    results_found: !!resultsFound,
                    num_search_results: resultsFound,
                    state: "results",
                  });
                  getSearchResultsFromAlgolia(search);
                }}
                aria-label="Search"
              >
                <SearchIcon className="text-nova-brand-primary-100 w-8 h-8" />
              </button>
            </div>
          </div>
          {Boolean(search.length) && (
            <QueryText
              query={search}
              items={suggestedHits}
              data={searchData}
              onSuggestedClicked={({ query }) => {
                GA4.onSearchEntered({
                  name: "Search",
                  location: "navigation",
                  state: searchState,
                  search_term: query,
                });
                GA4.onSearchResultsShown({
                  name: "Search",
                  location: "navigation",
                  search_term: query,
                  num_search_results: resultsFound,
                  results_found: !!resultsFound,
                  state: "results",
                });
                setSearch(query);
              }}
            />
          )}
        </div>
        {isLoadingSearch ? (
          <LoadingSpinner />
        ) : (
          <SearchResults
            data={searchData}
            querySearch={search}
            searchedItem={searchedItem}
            setSearchItem={setSearchItem}
            // suggestedHits={suggestedHits}
            isBottom={isBottom}
          />
        )}
      </div>
    </div>
  );
};

export default Search;
